import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useConnectionStore from './connectionStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Store Apollo client reference
let apolloClient = null;

export const setApolloClient = (client) => {
  apolloClient = client;
};

// Token refresh timeout ID
let refreshTimeoutId = null;

// Decode JWT and check if it's expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setHasHydrated: (value) => set({ _hasHydrated: value }),

      // Computed value - checks if token exists and is valid
      isAuthenticated: () => {
        const token = get().token;
        return !!token;
      },

      // Prefer JWT claims when persisted user is stale (e.g. role upgraded later)
      getAuthUser: () => {
        const { user, token } = get();
        if (!token || isTokenExpired(token)) return user;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            ...(user || {}),
            id: user?.id || payload.id,
            email: user?.email || payload.email,
            username: user?.username || payload.username,
            role: payload.role || user?.role,
            level: payload.level ?? user?.level,
          };
        } catch {
          return user;
        }
      },

      isAdmin: () => {
        const authUser = get().getAuthUser();
        if (!authUser) return false;
        if (authUser.role === 'admin') return true;
        const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '')
          .split(',')
          .map((email) => email.trim().toLowerCase())
          .filter(Boolean);
        const email = (authUser.email || '').toLowerCase();
        return Boolean(email && adminEmails.includes(email));
      },

      // Refresh token before expiration
      refreshToken: async () => {
        const token = get().token;
        if (!token) return;

        try {
          const response = await fetch(`${API_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            // Token refresh failed, sign out user
            get().signout();
            return;
          }

          // Update token
          set({ token: data.accessToken });

          // Schedule next refresh (refresh 5 minutes before expiration)
          // Assuming 2-hour token expiry, refresh after 1 hour 55 minutes
          get().scheduleTokenRefresh();
        } catch (error) {
          console.error('Token refresh error:', error);
          // On error, sign out user
          get().signout();
        }
      },

      // Schedule token refresh
      scheduleTokenRefresh: () => {
        // Clear existing timeout
        if (refreshTimeoutId) {
          clearTimeout(refreshTimeoutId);
        }

        // Schedule refresh in 1 hour 55 minutes (before 2-hour expiry)
        refreshTimeoutId = setTimeout(() => {
          get().refreshToken();
        }, 1 * 60 * 60 * 1000 + 55 * 60 * 1000); // 1h 55m
      },

      // Sign up action
      signup: async (username, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || data.errors?.[0]?.msg || 'Signup failed');
          }

          set({ 
            user: data.user, 
            token: data.accessToken,
            isLoading: false,
            error: null 
          });

          // Fetch candidate states after signup
          useConnectionStore.getState().fetchCandidateStates(data.accessToken);

          return { success: true, user: data.user };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Sign in action
      signin: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || data.errors?.[0]?.msg || 'Sign in failed');
          }

          set({ 
            user: data.user, 
            token: data.accessToken,
            isLoading: false,
            error: null 
          });

          // Schedule token refresh
          get().scheduleTokenRefresh();

          // Fetch candidate states after signin
          useConnectionStore.getState().fetchCandidateStates(data.accessToken);

          return { success: true, user: data.user };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Sign out action
      signout: async () => {
        const currentToken = get().token;
        
        // Clear refresh timeout
        if (refreshTimeoutId) {
          clearTimeout(refreshTimeoutId);
          refreshTimeoutId = null;
        }
        
        // Call logout endpoint to delete session from database
        if (currentToken) {
          try {
            await fetch(`${API_URL}/auth/logout`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${currentToken}`
              }
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        }

        useConnectionStore.getState().clearStates();
        set({ user: null, token: null, error: null });
        
        // Clear Apollo cache
        if (apolloClient) {
          apolloClient.clearStore().catch(err => {
            console.error('Error clearing Apollo cache:', err);
          });
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Update auth state with user and token (used after email verification)
      setAuth: (user, token) => {
        set({ user, token });
        // Schedule token refresh
        get().scheduleTokenRefresh();
        // Fetch candidate states after auth
        useConnectionStore.getState().fetchCandidateStates(token);
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token
      }),
      onRehydrateStorage: () => (state) => {
        // Called after rehydration (or if storage is empty)
        useAuthStore.setState({ _hasHydrated: true });
        if (state?.setHasHydrated) state.setHasHydrated(true);
      },
    }
  )
);

// Safety: unlock routes if rehydration callback never runs
if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (!useAuthStore.getState()._hasHydrated) {
      useAuthStore.setState({ _hasHydrated: true });
    }
  }, 150);
}

export default useAuthStore;
